import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import InputComponent from "../../components/InputComponent";  // Assumindo que este é o componente com todas as funcionalidades
import CheckboxComponent from "../../components/CheckboxComponent";
import Toast from "../../helpers/Toast";
import { useIntl } from "react-intl";
import { CheckListModelTaskService } from "../../services/CheckListModelTaskService";
import { EditCheckListModelTaskDTO, EditCheckListModelTaskDTOSchema } from "../../models/CheckListModelTask/EditCheckListModelTaskDTO";
import { CheckListModelTaskDTO } from "../../models/CheckListModelTask/CheckListModelTaskDTO";

interface Props {
    isOpen: boolean;
    toggle: () => void;
    taskId: number;
    checkListModelId: number;
    onUpdate: () => void;
}

const CheckListModelTaskEditModal: React.FC<Props> = ({ isOpen, toggle, taskId, checkListModelId, onUpdate }) => {
    const intl = useIntl();
    const [task, setTask] = useState<CheckListModelTaskDTO | null>(null);
    const [form, setForm] = useState<EditCheckListModelTaskDTO>({
        id: taskId,
        title: "",
        description: "",
        order: 0,
        active: true,
        checkListModelId: checkListModelId
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadTask = async () => {
        setLoading(true);
        const service = new CheckListModelTaskService();
        const response = await service.GetById(taskId);
        if (!response.success || !response.obj) {
            Toast.Show("error", response.message);
            setLoading(false);
            return;
        }
        setTask(response.obj);
        setForm({
            id: response.obj.id,
            title: response.obj.title,
            description: response.obj.description,
            order: response.obj.order,
            active: response.obj.active,
            checkListModelId: checkListModelId
        });
        setLoading(false);
    };

    useEffect(() => {
        if (isOpen) {
            loadTask();
        }
    }, [isOpen]);

    const handleSave = async () => {
        setSaving(true);

        // Validação do formulário
        const { error } = EditCheckListModelTaskDTOSchema.validate(form);
        if (error) {
            Toast.Show("error", error.details.map(e => e.message).join(", "));
            setSaving(false);
            return;
        }

        const service = new CheckListModelTaskService();
        const response = await service.Edit(form);
        setSaving(false);

        if (!response.success) {
            Toast.Show("error", response.message);
            return;
        }

        Toast.Show("success", response.message);
        onUpdate();
        toggle(); // Close the modal
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                {loading ? intl.formatMessage({ id: "Loading" }) : intl.formatMessage({ id: "Screens.CheckListModelTask.EditTitle" })}
            </ModalHeader>
            <ModalBody>
                {loading ? (
                    <div className="text-center">
                        <Spinner /> {intl.formatMessage({ id: "Loading" })}
                    </div>
                ) : (
                    <>
                        <InputComponent
                            xl={12} lg={12} md={12} sm={12} xs={12}
                            type="text"
                            value={form.title}
                            label={intl.formatMessage({ id: "Screens.CheckListModelTask.Edit.Info.Title" })}
                            onChange={(value: string) => setForm(prev => ({ ...prev, title: value }))}  // Atualizando o valor no form
                        />
                        <InputComponent
                            xl={12} lg={12} md={12} sm={12} xs={12}
                            type="textArea"
                            value={form.description}
                            label={intl.formatMessage({ id: "Screens.CheckListModelTask.Edit.Info.Description" })}
                            onChange={(value: string) => setForm(prev => ({ ...prev, description: value }))}
                            readOnly={false}
                        />
                        <InputComponent
                            xl={12} lg={12} md={12} sm={12} xs={12}
                            type="number"
                            value={(form.order ?? 0).toString()}
                            label={intl.formatMessage({ id: "Screens.CheckListModelTask.Edit.Info.Order" })}
                            onChange={(value: string) => setForm(prev => ({ ...prev, order: Number(value) }))}  // Garantindo que o valor é numérico
                        />
                        <CheckboxComponent
                            checked={form.active}
                            label={intl.formatMessage({ id: "Screens.CheckListModelTask.Edit.Info.Active" })}
                            onChange={(checked: boolean) => setForm(prev => ({ ...prev, active: checked }))}  // Atualizando o valor no form
                        />
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    {intl.formatMessage({ id: "Common.Buttons.Cancel" })}
                </Button>
                <Button color="primary" onClick={handleSave} disabled={saving}>
                    {saving ? intl.formatMessage({ id: "General.Loading" }) : intl.formatMessage({ id: "Common.Buttons.Save" })}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CheckListModelTaskEditModal;
